﻿// #region: Class definition *********************************************************************/
/**
 * A small, nonblocking notification pop-up.
 * A toast is shown to users with readable message content at a specific target and disappears automatically after a time-out.
 * The toast also allows to be manually closed when its set to be 'closable'.
 * 
 * Depends on the DOMPurify library. 
 */
class toast {

    // #region: Static Data **********************************************************************/
    /** Where do we position the toast message. */
    static toastPosition = {
        TopLeft: "top-left",
        TopCenter: "top-center",
        TopRight: "top-right",
        CenterLeft: "center-left",
        CenterCenter: "center-center",
        CenterRight: "center-right",
        BottomLeft: "bottom-left",
        BottomCenter: "bottom-center",
        BottomRight: "bottom-right"
    };
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor() {

        /** Used to set the allowed classes on the positioning grid (the location of the actual toast). */
        this.toastPositionIndex = [
            [toast.toastPosition.TopLeft, toast.toastPosition.TopCenter, toast.toastPosition.TopRight],
            [toast.toastPosition.CenterLeft, toast.toastPosition.CenterCenter, toast.toastPosition.CenterRight],
            [toast.toastPosition.BottomLeft, toast.toastPosition.BottomCenter, toast.toastPosition.BottomRight]
        ];

        /** The inline svg icons on the respective toast types. */
        this.svgs = {
            // check-circle
            success: '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"><path d="m424-408-86-86q-11-11-28-11t-28 11q-11 11-11 28t11 28l114 114q12 12 28 12t28-12l226-226q11-11 11-28t-11-28q-11-11-28-11t-28 11L424-408Zm56 328q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/></svg>',
            // warning
            warn: '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"><path d="M109-120q-11 0-20-5.5T75-140q-5-9-5.5-19.5T75-180l370-640q6-10 15.5-15t19.5-5q10 0 19.5 5t15.5 15l370 640q6 10 5.5 20.5T885-140q-5 9-14 14.5t-20 5.5H109Zm371-120q17 0 28.5-11.5T520-280q0-17-11.5-28.5T480-320q-17 0-28.5 11.5T440-280q0 17 11.5 28.5T480-240Zm0-120q17 0 28.5-11.5T520-400v-120q0-17-11.5-28.5T480-560q-17 0-28.5 11.5T440-520v120q0 17 11.5 28.5T480-360Z"/></svg>',
            // info
            info: '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"><path d="M480-280q17 0 28.5-11.5T520-320v-160q0-17-11.5-28.5T480-520q-17 0-28.5 11.5T440-480v160q0 17 11.5 28.5T480-280Zm0-320q17 0 28.5-11.5T520-640q0-17-11.5-28.5T480-680q-17 0-28.5 11.5T440-640q0 17 11.5 28.5T480-600Zm0 520q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/></svg>',
            // cancel
            error: '<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px"><path d="m480-424 116 116q11 11 28 11t28-11q11-11 11-28t-11-28L536-480l116-116q11-11 11-28t-11-28q-11-11-28-11t-28 11L480-536 364-652q-11-11-28-11t-28 11q-11 11-11 28t11 28l116 116-116 116q-11 11-11 28t11 28q11 11 28 11t28-11l116-116Zm0 344q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z"/></svg>'
        };

        /** The default options, that can be overridden by the caller. */
        this.defaultOptions = {
            callback: undefined,
            closable: true,
            duration: 4000,
            focusable: true,
            offsetBlock: 0,
            offsetInline: 0,
            position: toast.toastPosition.TopCenter,
            purify: true,
            title: undefined
        };
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {string}                        The string representation of this class.
     */
    toString() {
        return "[toast]";
    }

    static toString() {
        return "[toast]";
    }

    /** 
     * Identifies the version.
     * @returns {string}                        The version number of this class.
     */
    static version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Hide/delete the toast.
     */
    hide(toastCard) {
        toastCard.style.setProperty(`margin-${toastCard.options.yPos}`, `-${toastCard.offsetHeight}px`);
        toastCard.style.setProperty("opacity", "0");

        setTimeout(() => {
            toastCard.remove()

            if (typeof toastCard.options.callback === "function") {
                toastCard.options.callback();
            }
        }, 500);
    }

    /**
     * Show an error message.
     * @param {String} message                  The message to display; can contain html.
     * @param {Object} options                  The options for this toast.
     */
    error(message, options) {
        return this.show(message, options, "error");
    }

    /**
     * Show an informational message.
     * @param {String} message                  The message to display; can contain html.
     * @param {Object} options                  The options for this toast.
     */
    info(message, options) {
        return this.show(message, options, "info");
    }

    /**
     * Show a success message.
     * @param {String} message                  The message to display; can contain html.
     * @param {Object} options                  The options for this toast.
     */
    success(message, options) {
        return this.show(message, options, "success");
    }

    /**
     * Show a warning message.
     * @param {String} message                  The message to display; can contain html.
     * @param {Object} options                  The options for this toast.
     */
    warn(message, options) {
        return this.show(message, options, "warn");
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Appends the toast container when it does not exist.
     */
    appendContainer() {
        const container = document.querySelector(".toast-container");

        if (!container) {
            // Render and append the container.
            const toastContainer = document.createElement("div");
            toastContainer.className = "toast-container";

            // Create the positioning grid.
            for (const ri of [0, 1, 2]) {
                const row = document.createElement("div");
                row.className = "toast-row";

                for (const ci of [0, 1, 2]) {
                    const col = document.createElement("div");
                    col.className = `toast-col ${this.toastPositionIndex[ri][ci]}`;
                    row.appendChild(col);
                }

                toastContainer.appendChild(row);
            }

            document.body.appendChild(toastContainer);
        }
    }

    /**
     * Hides the toast message after the duration set in the options.
     * If duration is '0' the toast will not auto hide.
     * @param {HTMLDivElement} toastCard        The toast message.
     */
    autoHide(toastCard) {
        if (toastCard?.options?.duration > 0) {
            setTimeout(() => {
                if (!toastCard.options.isFocus) {
                    this.hide(toastCard);
                }
            }, toastCard.options.duration);
        }
    }

    /**
     * Set events on the toast.
     * @param {HTMLDivElement} toastCard        The toast message.
     */
    setCardBindEvents(toastCard) {
        if (toastCard.options.closable) {
            toastCard.addEventListener("click", () => {
                this.hide(toastCard);
            });
        }

        toastCard.addEventListener("mouseover", () => {
            toastCard.options.isFocus = toastCard.options.focusable;
        });

        toastCard.addEventListener("mouseout", () => {
            toastCard.options.isFocus = false;
            this.autoHide(toastCard);
        });
    }

    /**
     * Append the content to the toast container.
     * @param {HTMLDivElement} toastCard        The toast message.
     */
    setCardContent(toastCard) {
        const textGroupDiv = document.createElement("div");
        textGroupDiv.className = "toast-card-text";

        if (toastCard.options.title) {
            textGroupDiv.innerHTML = `<h4>${toastCard.options.title}</h4>`
        }

        textGroupDiv.innerHTML += `<p>${toastCard.options.message}</p>`;
        toastCard.appendChild(textGroupDiv);
    }

    /**
     * Add the animation style(s) on the toast.
     * @param {HTMLDivElement} toastCard        The toast message.
     */
    setCardIntroAnimation(toastCard) {
        toastCard.style.setProperty(`margin-${toastCard.options.yPos}`, "-8px");
        toastCard.style.setProperty("opacity", "0");

        setTimeout(() => {
            toastCard.style.setProperty(`margin-${toastCard.options.yPos}`, "8px")
            toastCard.style.setProperty("opacity", "1")
        }, 50);
    }

    /**
     * Actually shows the given message on the 'type' toast configured by the options.
     * @param {String} message                  The message to display; can contain html.
     * @param {Object} options                  The options for this toast.
     * @param {String} type                     The type of message to display, default to "warn".
     * @returns {HTMLDivElement}                The generated toast so the caller can modify html/style/events.  
     */
    show(message = "", options, type) {
        options = { ...this.defaultOptions, ...options };

        // Make sure the container exists.
        this.appendContainer();

        const toastType = type || "warn";
        const col = document.getElementsByClassName(options.position)[0];
        const toastCard = document.createElement("div");
        const toastMessage = options.purify ? DOMPurify.sanitize(message, { USE_PROFILES: { html: true }, ADD_ATTR: ["target"] }) : message;

        toastCard.className = `toast-card toast-type-${toastType}`;
        toastCard.innerHTML += this.svgs[toastType];
        toastCard.options = {
            ...options,
            ...{
                message: toastMessage,
                type: toastType,
                yPos: options.position.indexOf("top") > -1 ? "top" : "bottom",
                isFocus: false
            }
        };

        this.setCardContent(toastCard);
        this.setCardIntroAnimation(toastCard);
        this.setCardBindEvents(toastCard);
        this.autoHide(toastCard);

        // Create close element.
        if (toastCard.options.closable) {
            const closeElem = document.createElement("span");
            closeElem.classList.add("toast-card-close");
            closeElem.classList.add("lf-icon-close");
            toastCard.appendChild(closeElem);
        }

        toastCard.close = () => {
            this.hide(toastCard);
        }

        // Offset.
        toastCard.style.insetBlockStart = toastCard.getBoundingClientRect().top + options.offsetBlock + "px";
        toastCard.style.insetInlineStart = toastCard.getBoundingClientRect().left + options.offsetInline + "px";

        col.appendChild(toastCard);

        return toastCard;
    }
    // #endregion ********************************************************************************/

};
// #endregion ************************************************************************************/